let accessToken = null;
let onSessionExpired = null;

export const authStore = {
  getToken: () => accessToken,
  setToken: (t) => (accessToken = t),
  clear: () => (accessToken = null),
  // Called when a refresh attempt fails mid-session (refresh token expired
  // or revoked) so the app can reset its auth state.
  setOnSessionExpired: (fn) => (onSessionExpired = fn),
};

export function createApiClient(baseUrl = "") {
  // Single-flight: if several requests hit 401 at once, only one refresh
  // call goes out and the rest await the same promise.
  let refreshPromise = null;

  const refreshAccessToken = () => {
    if (!refreshPromise) {
      refreshPromise = (async () => {
        const res = await fetch(`${baseUrl}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Session expired");
        const { access_token } = await res.json();
        accessToken = access_token;
        return access_token;
      })().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  };

  return async function apiFetch(url, options = {}) {
    const fullUrl = `${baseUrl}${url}`;

    const doFetch = () => {
      const headers = {
        "Content-Type": "application/json",
        ...options.headers,
      };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      return fetch(fullUrl, { ...options, headers, credentials: "include" });
    };

    let res = await doFetch();

    // Access token expired mid-session: refresh once and retry the request.
    if (res.status === 401 && accessToken) {
      try {
        await refreshAccessToken();
        res = await doFetch();
      } catch {
        authStore.clear();
        if (onSessionExpired) onSessionExpired();
      }
    }

    // Handle non-JSON safely
    let data;
    try {
      data = await res.json();
    } catch {
      data = null;
    }

    if (!res.ok) {
      const err = new Error(data?.detail || "API error");
      err.status = res.status;
      throw err;
    }

    return data;
  };
}
